/* eslint-disable jsdoc/require-jsdoc */
import { beginAuthJourney, setActiveAuthJourney } from '#~/auth/index.js';
import { createAuthJourneyTracker } from '#~/analytics/domains/index.js';
import { useAnalytics } from '#~/analytics/index.js';
import { Paths } from '../../constants.js';
import { Anchor } from '@studio/design-system/button';
import { Icon } from '@studio/design-system/icon';
import { useMemo } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sel, STagNew } from '#~/stag/index.js';
import {
  EXPIRED_INVITE_ERROR_CODES,
  INVITE_ERROR_CODES,
  LEGACY_INVITE_ERROR_KEYWORDS,
  openVendorSupport
} from './invite-error-utils.js';

type InviteErrorConfig = {
  body: ReactNode;
  showAuthLinks: boolean;
  showReturnAction: boolean;
  title: string;
};

type GetInviteErrorConfigArgs = {
  code: number;
  errorMessage?: string | undefined;
  inviteeObscuredEmail?: string | undefined;
};

const normalizeInviteErrorCode = (code: number, errorMessage?: string | undefined): number => {
  if (code !== INVITE_ERROR_CODES.LEGACY_BAD_REQUEST || !errorMessage) {
    return code;
  }

  const normalizedMessage = errorMessage.toLowerCase();

  if (normalizedMessage.includes(LEGACY_INVITE_ERROR_KEYWORDS.REDEEMED)) {
    return INVITE_ERROR_CODES.ALREADY_REDEEMED;
  }

  if (normalizedMessage.includes(LEGACY_INVITE_ERROR_KEYWORDS.INVALID)) {
    return INVITE_ERROR_CODES.INVALID_TOKEN;
  }

  return code;
};

export const getInviteErrorConfig = ({
  code,
  errorMessage,
  inviteeObscuredEmail
}: GetInviteErrorConfigArgs): InviteErrorConfig => {
  const normalizedCode = normalizeInviteErrorCode(code, errorMessage);

  if (normalizedCode === INVITE_ERROR_CODES.ALREADY_REDEEMED) {
    return {
      title: 'No Longer Part of This Studio',
      body: 'You are no longer part of this studio.',
      showAuthLinks: false,
      showReturnAction: true
    };
  }

  if (normalizedCode === INVITE_ERROR_CODES.NOT_FOUND) {
    return {
      title: 'Studio not found',
      body: (
        <>
          This Studio is no longer available. It may have been deleted or the invite was revoked.
          Please contact the Studio owner or our{' '}
          <Anchor
            className='inline text-primary hover:text-primary-highlight'
            onClick={() => {
              openVendorSupport();
            }}
          >
            support.
          </Anchor>
        </>
      ),
      showAuthLinks: false,
      showReturnAction: true
    };
  }

  if (EXPIRED_INVITE_ERROR_CODES.has(normalizedCode)) {
    return {
      title: 'Invitation Expired',
      body: 'You are using an expired invite.',
      showAuthLinks: false,
      showReturnAction: true
    };
  }

  if (normalizedCode === INVITE_ERROR_CODES.WRONG_EMAIL) {
    return {
      title: 'Wrong Email',
      body: inviteeObscuredEmail
        ? `This invitation is linked to the email address ${inviteeObscuredEmail}. Please login or create a new account with the correct email address to accept the invitation.`
        : 'This invitation is linked to a different email address. Please login or create a new account with the correct email address to accept the invitation.',
      showAuthLinks: true,
      showReturnAction: false
    };
  }

  if (normalizedCode === INVITE_ERROR_CODES.HIGHER_ROLE) {
    return {
      title: 'Higher Role Exists',
      body: 'You already have a higher role within this Studio than the one offered in this invite.',
      showAuthLinks: true,
      showReturnAction: false
    };
  }

  if (normalizedCode === INVITE_ERROR_CODES.WRONG_USER_ID) {
    return {
      title: 'Wrong User ID',
      body: "You do not have permission to redeem this invitation. Please make sure you're logged into the correct account.",
      showAuthLinks: true,
      showReturnAction: false
    };
  }

  if (normalizedCode === INVITE_ERROR_CODES.ACCESS_DENIED) {
    return {
      title: 'Access Denied',
      body: "You do not have permission to redeem this invitation. It may have been revoked, expired, or linked with a different email address. Please make sure you're logged into the correct account and try again.",
      showAuthLinks: true,
      showReturnAction: false
    };
  }

  if (normalizedCode === INVITE_ERROR_CODES.SESSION_INVALID) {
    return {
      title: 'Session Invalid',
      body: "Oops! There's an issue redeeming this invite. Please make sure you're logged into the correct account, or try reaching out to an Admin to resend the invitation.",
      showAuthLinks: true,
      showReturnAction: false
    };
  }

  if (normalizedCode === INVITE_ERROR_CODES.SERVER_ERROR) {
    return {
      title: 'Something Went Wrong',
      body: "Oops! Looks like something went wrong. Please make sure you're logged into the correct account, or try back again later.",
      showAuthLinks: true,
      showReturnAction: false
    };
  }

  return {
    title: 'Something Went Wrong',
    body: 'Oops! Unknown error.',
    showAuthLinks: true,
    showReturnAction: false
  };
};

type InviteErrorProps = {
  code: number;
  errorMessage?: string | undefined;
  inviteeObscuredEmail?: string | undefined;
};

export const InviteError = ({
  code,
  errorMessage,
  inviteeObscuredEmail
}: InviteErrorProps): ReactElement => {
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const authJourneyTracker = useMemo(() => createAuthJourneyTracker(track), [track]);
  const config = useMemo(
    () => getInviteErrorConfig({ code, errorMessage, inviteeObscuredEmail }),
    [code, errorMessage, inviteeObscuredEmail]
  );

  const navigateToAuthRoute = (source: 'studio_login' | 'studio_register'): void => {
    const journey = beginAuthJourney(source);

    setActiveAuthJourney(journey);

    if (source === 'studio_register') {
      authJourneyTracker.trackCreateAccountClicked(journey, {
        surface: 'invite_error'
      });
    } else {
      authJourneyTracker.trackContinueWithEmailClicked(journey, {
        surface: 'invite_error'
      });
    }

    navigate(`/${source === 'studio_register' ? Paths.register : Paths.login}`, {
      state: { authJourney: journey }
    });
  };

  return (
    <div className='p-32 text-center' {...STagNew(Sel.Notifications, 'invite_error')}>
      <div className='mb-12'>
        <Icon className='text-[86px] text-error' icon='alert-triangle' />
      </div>
      <div>
        <h1>{config.title}</h1>
        <p>{config.body}</p>
      </div>
      {config.showReturnAction && (
        <div className='mt-18 flex justify-center'>
          <a className='text-primary' href='/'>
            Go back to my studio
          </a>
        </div>
      )}
      {config.showAuthLinks && (
        <div className='mt-18 flex justify-center gap-x-4'>
          <button
            className='text-primary'
            type='button'
            onClick={() => {
              navigateToAuthRoute('studio_register');
            }}
          >
            Create Account
          </button>
          or
          <button
            className='text-primary'
            type='button'
            onClick={() => {
              navigateToAuthRoute('studio_login');
            }}
          >
            Sign In
          </button>
        </div>
      )}
    </div>
  );
};
